<template>
  <div :class="mainWrapperClass">
    <div :class="$modal.modalContent">
      <form @submit.prevent="formAction">
        <v-flex>
          <p :class="$modal.modalText">
            Enter your password below:
          </p>
        </v-flex>
        <v-flex :class="[$modal.modalInput, $modal.shortInput]">
          <InputField :class="{[$modal.errorField] : $v.password.$error }"
                      v-model="$v.password.$model"
                      :type="show ? 'text' : 'password'"
                      :placeholder="isMobilePlatform ? '*******' : ''"
                      label="Password*"/>
          <button :class="$modal.passVisibility"
                  type="button"
                  @click.prevent="show = !show">
            {{ showPassMessage() }}
          </button>
          <p v-if="$v.password.$error"
             :class="$modal.errorMessage">Incorrect PIN - Please try again</p>
          <p v-if="errors"
             :class="$modal.errorMessage">
            {{ errors }}
          </p>
        </v-flex>
        <MobileButton v-if="isMobilePlatform"
                      :class="$modal.mobileBtn"
                      :disabled="pending"
                      @click="formAction">
          Submit
        </MobileButton>
        <p v-if="beforeLoginMessage"
           :class="$modal.boldMessage">
          {{ beforeLoginMessage }}
        </p>
        <button :class="$modal.forgotBtn"
                type="button"
                @click="forgotPassword">Forgot Password</button>
        <v-flex v-if="!isMobilePlatform"
                :class="$modal.modalButtons">
          <BaseButton :button-style="'Main'"
                      :class="$modal.submitBtn"
                      @click="formAction">Submit
          </BaseButton>
          <button :class="$modal.cancelBtn"
                  type="button"
                  @click="close()">Cancel
          </button>
        </v-flex>
      </form>
    </div>

    <LoadingSpinner v-if="isLoading"/>
  </div>
</template>

<script>
import Component from 'vue-class-component';
import Vue from 'vue';
import { required } from 'vuelidate/lib/validators';
import { Action, Getter } from 'vuex-class';
import BaseButton from '@/components/BaseButton';
import MobileButton from '@/apps/mobile/components/MobileButton';
import authMixins from '@/apps/auth/mixins';
import { AUTH_CUSTOMER_TOKEN } from '@/apps/auth/store/types';
import InputField from '@/components/InputField';
import { CART_CALC_ORDER, CART_PREPARE_ORDER } from '@/apps/cart/store/types';
import LoadingMixin from '@/lib/mixins/loading-mixin';
import LoadingSpinner from '@/apps/configurator/components/LoadingSpinner';

/**
 * From to perform login via saved phone number and passed password.
 *
 * Data:
 *
 * password - stores password from input.
 */
@Component({
  name:       'Login',
  components: {
    LoadingSpinner,
    BaseButton,
    InputField,
    MobileButton,
  },
  mixins: [
    authMixins.AuthorizationMixin,
    LoadingMixin,
  ],
  validations: {
    password: { required },
  },
  props: {
    checkoutAfterLogin: {
      type:    Boolean,
      default: false,
    },
  },
})
export default class Login extends Vue {
  password = '';
  show = false;

  @Action(AUTH_CUSTOMER_TOKEN) vuexAction;
  @Getter authPhone;
  @Getter isMobilePlatform;
  @Getter pending;
  @Getter cartIsEmpty;
  /** @type {string} */
  @Getter beforeLoginMessage;
  @Getter authUser;
  @Getter guestAccountData;
  @Getter getPreparedItems;
  @Getter getNotAuthUserData;
  @Getter restaurantId;
  @Getter orderType;
  @Getter order;
  @Action(CART_PREPARE_ORDER) prepareOrder;
  @Action(CART_CALC_ORDER) cartCalcOrder;

  get mainWrapperClass() {
    const mobileWrapperClass = this.isMobilePlatform ? this.$modal.modalMobile : '';

    return [ this.$modal.modalContentContainer, mobileWrapperClass ];
  }

  /**
   * Pass data into api call.
   * @returns {{password: string, phone: String}}
   */
  getData() {
    return {
      phone:    this.authPhone,
      password: this.password,
    };
  }

  /**
   * Emits close event on successful action finish and stores response in
   * components data. Close event should be
   * handled on the parent component to close modal.
   * @param userData - User data.
   */
  async handleSuccess(userData) {
    const name = this.isMobilePlatform ? 'mobile-main' : 'menu';

    if (this.checkoutAfterLogin) {
      try {
        this.loadingStart();
        await this.prepareOrder({ userData });
        this.$emit('authorized');

        return this.$router.push({ name: 'payment-confirm', query: { backPage: 'menu' } });
      } catch (e) {
        return this.$router.push({ name });
      } finally {
        this.loadingStop();
      }
    }

    if (!this.cartIsEmpty) {
      try {
        this.loadingStart();

        const tempUserData =
          this.authUser ||
          this.getNotAuthUserData ||
          this.guestAccountData;
        const order = {
          restaurant_id: this.restaurantId,
          pickup_type:   this.orderType,
          first_name:    tempUserData.firstName,
          last_name:     tempUserData.lastName,
          phone:         tempUserData.phone,
          email:         tempUserData.email,
          items:         this.getPreparedItems,
        };

        await this.cartCalcOrder();
        await this.prepareOrder(order);
      } catch (error) {
        console.error(error);
      } finally {
        this.loadingStop();
      }
    }

    this.$emit('authorized');

    return this.$router.push({ name });
  }

  showPassMessage() {
    if (this.show) {
      return 'Hide';
    }

    return 'Show';
  }

  /**
   * Emits forgot password event.
   * Close event should be handled on the parent component to close modal.
   * */
  forgotPassword() {
    this.$emit('forgotPassword');
    // TODO: need add redirect for another platform.

    const name = this.isMobilePlatform ? 'forgot-password' : '';

    this.$router.push(name);
  }
}
</script>

<style src="@/style/modals.scss" lang="scss" module="$modal"></style>



// WEBPACK FOOTER //
// src/apps/auth/components/Login.vue